<template>
  <Dialog
    :open="isMeetingModalOpenRef"
    :modal="false"
    @update:open="onDialogOpenChange"
  >
    <DialogPortal>
      <DialogOverlay
        v-if="!isResizedRef"
        class="fixed inset-0 z-40 bg-black/50 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
      />
      <DialogContent
        :ref="setContentRef"
        data-test-id="meeting-dialog"
        :style="isResizedRef ? dragStyle : undefined"
        :class="[
          'fixed z-50 overflow-hidden rounded-lg bg-black shadow-lg outline-none',
          'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
          isResizedRef
            ? 'h-40 aspect-video md:h-48 select-none'
            : 'inset-3 md:inset-6 transition-all duration-200 ease-in-out',
        ]"
        @escape-key-down.prevent
        @pointer-down-outside.prevent
        @interact-outside.prevent
      >
        <div
          v-if="isResizedRef"
          ref="dragHandleRef"
          data-test-id="meeting-drag-handle"
          :class="[
            'absolute top-0 left-0 right-0 z-10 flex h-6 items-center justify-center bg-neutral-900',
            isDragging ? 'cursor-grabbing' : 'cursor-grab',
          ]"
        >
          <GripHorizontal class="size-4 text-white/70" />
        </div>

        <whereby-embed
          v-if="roomUrlRef"
          ref="wherebyRef"
          :room="roomUrlRef"
          :displayName="displayNameRef"
          lang="fr"
          minimal
          screenshare
          :class="['block h-full w-full', isResizedRef ? 'pt-6' : '']"
        />

        <div
          :class="[
            'absolute flex items-center gap-2',
            isResizedRef ? 'top-8 right-2' : 'bottom-3 left-3',
          ]"
        >
          <Button
            data-test-id="meeting-close-btn"
            size="icon"
            variant="destructive"
            class="rounded-full"
            @click="closeModal"
          >
            <PhoneOff class="size-4" />
          </Button>
          <Button
            data-test-id="meeting-resize-btn"
            size="icon"
            variant="secondary"
            class="rounded-full"
            @click="onClickResizeHandler"
          >
            <Maximize2 v-if="isResizedRef" class="size-4" />
            <Minimize2 v-else class="size-4" />
          </Button>
        </div>
      </DialogContent>
    </DialogPortal>
  </Dialog>
</template>

<script setup lang="ts">
import '@whereby.com/browser-sdk/embed'

import { useDraggable } from '@vueuse/core'
import { computed, nextTick, ref, watch, onBeforeUnmount } from 'vue'
import { GripHorizontal, Maximize2, Minimize2, PhoneOff } from 'lucide-vue-next'
import { DialogContent, DialogOverlay, DialogPortal } from 'reka-ui'
import { toast } from 'vue-sonner'

import { Button } from '@/components/ui/button'
import { Dialog } from '@/components/ui/dialog'
import useIsAuth from '@/features/auth/hooks/use-is-auth.hook'
import useMeeting from '@/features/meetings/hooks/use-meeting.hook'
import { trackMeetingEnter, trackMeetingExit, trackMeetingToggle } from '@/features/meetings/api'
import { selectGetMeetingModal } from '@/features/ui/store/modal/modal.selectors'
import type { MeetingModalType } from '@/features/ui/store/modal/modal.store'
import modalsStore from '@/features/ui/store/modal/modal.store'
import useRouter from '@/hooks/use-router.hook'
import { useI18n } from '@/lib/i18n'
import { selectGetCurrentRoute } from '@/store/route/route.selector'
import routeStore from '@/store/route/route.store'

const router = useRouter()
const { t } = useI18n()
const { isAuthRef } = useIsAuth()
const meetingModalRef = selectGetMeetingModal(modalsStore)
const isMeetingModalOpenRef = computed(() => !!meetingModalRef.value?.isOpen)
const isResizedRef = computed(() => !!meetingModalRef.value?.attachedData.isResized)

const wherebyRef = ref<HTMLElement | null>(null)
const trackingIdRef = ref<string | null>(null)
const isLeavingRef = ref(false)

const dialogContentEl = ref<HTMLElement | null>(null)
const dragHandleRef = ref<HTMLElement | null>(null)

function setContentRef(instance: unknown) {
  const el = (instance as { $el?: HTMLElement } | HTMLElement | null)
  if (!el) {
    dialogContentEl.value = null
    return
  }
  dialogContentEl.value = ('$el' in (el as object) ? (el as { $el: HTMLElement }).$el : (el as HTMLElement)) ?? null
}

const { x, y, style: dragStyle, isDragging } = useDraggable(dialogContentEl, {
  handle: dragHandleRef,
  initialValue: { x: 16, y: 16 },
  preventDefault: true,
  onMove(pos) {
    const el = dialogContentEl.value
    if (!el) return
    const maxX = Math.max(0, window.innerWidth - el.offsetWidth)
    const maxY = Math.max(0, window.innerHeight - el.offsetHeight)
    pos.x = Math.min(Math.max(0, pos.x), maxX)
    pos.y = Math.min(Math.max(0, pos.y), maxY)
  },
})

watch(isResizedRef, async (resized) => {
  if (!resized) return
  await nextTick()
  const el = dialogContentEl.value
  const h = el?.offsetHeight ?? 176
  x.value = 16
  y.value = Math.max(16, window.innerHeight - h - 16)
})

const currentRouteRef = selectGetCurrentRoute(routeStore)

const routerParamMeetingRoomIdRef = computed(
  () => (currentRouteRef.value?.query.meetingSessionId || currentRouteRef.value?.query.meetingRoomId) as string,
)

const activeMeetingRoomIdRef = ref<string | null>(null)
const activeMeetingRoomIdComputedRef = computed(() => activeMeetingRoomIdRef.value)

watch(
  routerParamMeetingRoomIdRef,
  (meetingRoomId) => {
    if (meetingRoomId && !isLeavingRef.value) {
      activeMeetingRoomIdRef.value = meetingRoomId
    }
  },
  { immediate: true },
)

const { roomUrlRef, displayNameRef } = useMeeting(activeMeetingRoomIdComputedRef)

watch(
  [activeMeetingRoomIdComputedRef, isMeetingModalOpenRef, isAuthRef, roomUrlRef],
  ([meetingRoomId, isMeetingModalOpen, isAuth, roomUrl]) => {
    if (meetingRoomId && !isMeetingModalOpen && isAuth && roomUrl && !isLeavingRef.value) {
      modalsStore.toggleModal('meeting', true)
    }
  },
  { immediate: true },
)

watch(wherebyRef, (el) => {
  if (!el) return
  el.addEventListener('join', onJoin)
  el.addEventListener('leave', onLeave)
  el.addEventListener('meeting_end', onLeave)
  el.addEventListener('microphone_toggle', onMicrophoneToggle)
  el.addEventListener('camera_toggle', onCameraToggle)
})

async function onJoin() {
  const meetingRoomId = routerParamMeetingRoomIdRef.value
  if (!meetingRoomId) return
  try {
    const result = await trackMeetingEnter(meetingRoomId)
    trackingIdRef.value = result.id
  } catch (err) {
    console.error('Failed to track meeting enter:', err)
  }
}

async function onLeave() {
  if (isLeavingRef.value) return
  isLeavingRef.value = true
  if (trackingIdRef.value) {
    try {
      await trackMeetingExit(trackingIdRef.value)
    } catch (err) {
      console.error('Failed to track meeting exit:', err)
    }
    trackingIdRef.value = null
  }
  leaveMeeting()
  isLeavingRef.value = false
}

async function onMicrophoneToggle() {
  if (!trackingIdRef.value) return
  try {
    await trackMeetingToggle(trackingIdRef.value, 'microphone')
  } catch (err) {
    console.error('Failed to track microphone toggle:', err)
  }
}

async function onCameraToggle() {
  if (!trackingIdRef.value) return
  try {
    await trackMeetingToggle(trackingIdRef.value, 'camera')
  } catch (err) {
    console.error('Failed to track camera toggle:', err)
  }
}

function leaveMeeting() {
  modalsStore.toggleModal('meeting', false)
  activeMeetingRoomIdRef.value = null
  toast.success(t('meeting.actions.left'))
  const currentQuery = { ...(router?.currentRoute?.query || {}) }
  delete currentQuery.meetingRoomId
  delete currentQuery.meetingSessionId
  router?.replace({ path: window.location.pathname, query: currentQuery }).catch(() => {}).finally(() => {
    isLeavingRef.value = false
  })
}

function closeModal() {
  if (isLeavingRef.value) return
  isLeavingRef.value = true
  leaveMeeting()
  if (trackingIdRef.value) {
    trackMeetingExit(trackingIdRef.value).catch((err) =>
      console.error('Failed to track meeting exit:', err)
    )
    trackingIdRef.value = null
  }
}

function onClickResizeHandler() {
  modalsStore.updateModalAttachedData<MeetingModalType>('meeting', { isResized: !isResizedRef.value })
}

function onDialogOpenChange(open: boolean) {
  if (!open && !isLeavingRef.value) {
    closeModal()
  }
}

onBeforeUnmount(() => {
  const el = wherebyRef.value
  if (el) {
    el.removeEventListener('join', onJoin)
    el.removeEventListener('leave', onLeave)
    el.removeEventListener('meeting_end', onLeave)
    el.removeEventListener('microphone_toggle', onMicrophoneToggle)
    el.removeEventListener('camera_toggle', onCameraToggle)
  }
})
</script>
